import React, { useEffect, useMemo, useState } from "react";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { logToSystem } from "../../utils/systemLogging";
import { db, auth } from "../../firebase/firebase";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";
import CustomDropdown from "../CustomDropdown";
import { getLeaveCalendarPermissions } from "../../utils/permissions";



type CalendarPageProps = {
  organizationId: string;

  bg?: string;
  color?: string;
  isAdmin?: boolean;
  currentUser?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  users?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>; // forwarded for details
  [key: string]: any;
};

type Holiday = {
  id: string; // YYYY-MM-DD
  date: string; // YYYY-MM-DD
  reason: string;
};

function formatYmd(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CalendarPage: React.FC<CalendarPageProps> = ({
  organizationId,
  bg,
  isAdmin,
  currentUser,
  users,
  ...rest
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const calendarPerms = useMemo(
    () => getLeaveCalendarPermissions(currentUser),
    [currentUser]
  );
  const canEdit = calendarPerms.canEdit;
  const canDelete = calendarPerms.canDelete;

  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth()); // 0-11
  const [holidays, setHolidays] = useState<Record<string, Holiday>>({});
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [modalState, setModalState] = useState<
    | { type: "create"; date: string; reason: string }
    | { type: "view"; date: string; reason: string; editing: boolean }
    | null
  >(null);
  const [selectedDateYmd, setSelectedDateYmd] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Quotes for empty month state
  const quotes = useMemo(
    () => [
      "Take rest; a field that has rested gives a bountiful crop.",
      "Almost everything will work again if you unplug it for a few minutes.",
      "Rest is not idleness; it is the doorway to clarity.",
      "Sometimes the most productive thing you can do is relax.",
      "Pause. Breathe. You are exactly where you need to be.",
      "In the rhythm of life, rest is a note worth playing.",
    ],
    []
  );
  const randomQuote = useMemo(
    () => quotes[Math.floor(Math.random() * quotes.length)],
    [quotes, month, year]
  );

  // Year options: rolling 5-year window centered on selected year
  const yearOptions = useMemo(() => {
    const start = year - 2;
    return Array.from({ length: 5 }, (_, idx) => start + idx);
  }, [year]);

  // Subscribe to holidays collection for this org
  useEffect(() => {
    if (!organizationId) return;
    const holidaysCol = collection(
      db,
      "organization",
      organizationId,
      "holidays"
    );
    const unsub = onSnapshot(holidaysCol, (snap) => {
      const byId: Record<string, Holiday> = {};
      snap.forEach((d) => {
        const data = d.data() as any;
        byId[d.id] = { id: d.id, date: data.date, reason: data.reason };
      });
      setHolidays(byId);
    });
    return () => unsub();
  }, [organizationId]);

  const firstDayOfMonth = useMemo(
    () => new Date(year, month, 1),
    [year, month]
  );
  const daysInMonth = useMemo(
    () => new Date(year, month + 1, 0).getDate(),
    [year, month]
  );
  const startWeekday = useMemo(
    () => firstDayOfMonth.getDay(),
    [firstDayOfMonth]
  ); // 0=Sun

  const daysGrid = useMemo(() => {
    const cells: Array<{ date: Date | null }>[] = [];
    const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
    const arr: Array<{ date: Date | null }> = [];
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startWeekday + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        arr.push({ date: null });
      } else {
        arr.push({ date: new Date(year, month, dayNum) });
      }
    }
    while (arr.length) cells.push(arr.splice(0, 7));
    return cells;
  }, [startWeekday, daysInMonth, year, month]);

  const handlePrevMonth = () => {
    const d = new Date(year, month - 1, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };
  const handleNextMonth = () => {
    const d = new Date(year, month + 1, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  const handleCreateHoliday = (dateYmd: string) => {
    setModalState({ type: "create", date: dateYmd, reason: "" });
  };

  const handleSaveCreate = async () => {
    if (!modalState || modalState.type !== "create") return;
    const { date, reason } = modalState;
    if (!organizationId || !date || !reason.trim()) return;
    const ref = doc(db, "organization", organizationId, "holidays", date);
    await setDoc(ref, { date, reason });

    // Log holiday creation activity
    try {
      const currentUserAuth = auth.currentUser;
      if (currentUserAuth && organizationId) {
        const userRef = doc(db, "users", currentUserAuth.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          await logToSystem(organizationId, "actions", {
            type: "ACTIVITY",
            user: `${userData.firstName} ${userData.lastName}`,
            email: userData.email,
            action: "Leave Calendar",
            details: `Added holiday: ${reason} on ${getDateDisplay(date)}`,
            status: "success",
          });
        }
      }
    } catch (logError) {
      console.error("Error logging holiday creation:", logError);
    }

    setModalState(null);
  };

  const handleOpenExisting = (dateYmd: string) => {
    const h = holidays[dateYmd];
    if (!h) return;
    setModalState({
      type: "view",
      date: h.date,
      reason: h.reason,
      editing: false,
    });
  };

  const handleUpdateHoliday = async () => {
    if (!modalState || modalState.type !== "view") return;
    const { date, reason } = modalState;
    if (!organizationId || !date || !reason.trim()) return;

    setIsUpdating(true);
    try {
      const ref = doc(db, "organization", organizationId, "holidays", date);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;

      // Get old reason before update
      const oldReason = snap.data()?.reason || "";

      await updateDoc(ref, { reason });

      // Log holiday update activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "Leave Calendar",
              details: `Updated holiday on ${getDateDisplay(date)}: Reason: From ${oldReason} To ${reason}`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging holiday update:", logError);
      }

      setModalState({ ...modalState, editing: false });
    } catch (error) {
      console.error("Error updating holiday:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!modalState || modalState.type !== "view") return;
    const { date, reason } = modalState;
    if (!organizationId || !date) return;
    const ref = doc(db, "organization", organizationId, "holidays", date);
    await deleteDoc(ref);

    // Log holiday deletion activity
    try {
      const currentUserAuth = auth.currentUser;
      if (currentUserAuth && organizationId) {
        const userRef = doc(db, "users", currentUserAuth.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          await logToSystem(organizationId, "actions", {
            type: "ACTIVITY",
            user: `${userData.firstName} ${userData.lastName}`,
            email: userData.email,
            action: "Leave Calendar",
            details: `Deleted holiday: ${reason} on ${getDateDisplay(date)}`,
            status: "success",
          });
        }
      }
    } catch (logError) {
      console.error("Error logging holiday deletion:", logError);
    }

    setModalState(null);
  };

  const getDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const holidayCount = Object.keys(holidays).length;

  return (
    <div className="w-full px-4 py-1" style={{ background: bg }} {...rest}>
      {/* Main Heading Section */}
      <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-6 border ${theme.border}`}>
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            Holiday Calendar
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            Manage organizational holidays and important dates. Click on any
            date to add or view holiday information.
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className={`text-sm px-3 py-2 rounded-full ${isDark ? 'text-white bg-[#14161B] border border-[#2E2F33]' : 'text-gray-600 bg-white border border-gray-200'}`}>
            <span className="font-medium">
              {
                Object.values(holidays).filter((h) => {
                  const holidayDate = new Date(h.date);
                  return (
                    holidayDate.getFullYear() === year &&
                    holidayDate.getMonth() === month
                  );
                }).length
              }
            </span>{" "}
            holidays this month
          </div>
        </div>
      </div>

      {/* Calendar Controls */}
      <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'} rounded-lg mb-4 overflow-hidden`}>
        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 ${isDark ? 'bg-[#14161B] border-b border-[#2E2F33]' : 'bg-gray-50 border-b border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevMonth}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full focus:outline-none transition-colors ${isDark ? 'text-[#FBD282] bg-[#1A1C1F] border border-[#B59143] hover:bg-[#2A3441] focus:ring-2 focus:ring-[#B59143]' : 'text-[#B59143] bg-white border border-[#E8C16C] hover:bg-[#FFF7E6] focus:ring-2 focus:ring-[#B59143] focus:ring-offset-2'}`}
              aria-label="Previous month"
              title="Previous month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="sr-only">Previous</span>
            </button>

            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {monthNames[month]} {year}
            </div>

            <button
              onClick={handleNextMonth}
              className={`inline-flex items-center justify-center w-9 h-9 rounded-full focus:outline-none transition-colors ${isDark ? 'text-[#FBD282] bg-[#1A1C1F] border border-[#B59143] hover:bg-[#2A3441] focus:ring-2 focus:ring-[#B59143]' : 'text-[#B59143] bg-white border border-[#E8C16C] hover:bg-[#FFF7E6] focus:ring-2 focus:ring-[#B59143] focus:ring-offset-2'}`}
              aria-label="Next month"
              title="Next month"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="sr-only">Next</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <CustomDropdown
              value={month.toString()}
              onChange={(value) => setMonth(parseInt(value))}
              isDark={isDark}
              className="rounded-lg px-3 py-2 focus:outline-none"
              options={monthNames.map((m, idx) => ({ value: idx.toString(), label: m }))}
            />
            <CustomDropdown
              value={year.toString()}
              onChange={(value) => setYear(parseInt(value))}
              isDark={isDark}
              className="rounded-lg px-3 py-2 focus:outline-none"
              options={yearOptions.map((y) => ({ value: y.toString(), label: y.toString() }))}
            />
            {/* Edit Mode Toggle - Only show for admins/editors/normal users */}
            {canEdit && (
              <button
                onClick={() => setIsEditMode(!isEditMode)}
                className={`inline-flex items-center gap-1 px-3 py-2 text-xs rounded-lg focus:outline-none transition-colors ${isDark ? (isEditMode ? 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-black border border-[#B59143]' : 'text-[#FBD282] bg-[#1A1C1F] border border-[#B59143] hover:bg-[#2A3441]') : (isEditMode ? 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-white border border-[#B59143]' : 'text-[#B59143] bg-white border border-[#E8C16C] hover:bg-[#FFF7E6]')} ${isDark ? 'focus:ring-1 focus:ring-[#B59143]' : 'focus:ring-1 focus:ring-[#B59143] focus:ring-offset-1'}`}
                aria-label={isEditMode ? "Exit edit mode" : "Enter edit mode"}
                title={isEditMode ? "Exit edit mode" : "Enter edit mode"}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Calendar + Details split */}
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Left: Calendar 66% on md+ */}
            <div className="md:basis-2/3 md:max-w-[66.666%]">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {[
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ].map((w) => (
                  <div
                    key={w}
                    className={`py-2 text-center text-sm font-semibold border-b ${isDark ? 'text-white border-[#2E2F33]' : 'text-gray-600 border-gray-100'}`}
                  >
                    <span className="hidden sm:inline">{w}</span>
                    <span className="sm:hidden">{w.slice(0, 3)}</span>
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {daysGrid.flat().map((cell, idx) => {
                  const d = cell.date;
                  const ymd = d ? formatYmd(d) : "";
                  const isHoliday = !!(d && holidays[ymd]);
                  const isToday = d && formatYmd(d) === formatYmd(today);
                  const isCurrentMonth = d !== null;

                  return (
                    <div
                      key={idx}
                      className={`
                        relative min-h-[44px] sm:min-h-[56px] rounded-md border transition-all duration-200 cursor-pointer
                        ${!isCurrentMonth
                          ? `${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-gray-50 border-gray-100'}`
                          : isHoliday
                            ? `${isDark ? 'bg-[#2A3441] border-[#3A4553] hover:bg-[#3A4553] hover:border-[#4A5463]' : 'bg-[#FFF7E6] border-[#E8C16C] hover:bg-[#FAEDD0] hover:border-[#A7843D]'} shadow-sm`
                            : isToday
                              ? `${isDark ? 'bg-[#3A4553] border-[#B59143] hover:bg-[#2A3441] hover:border-[#A7843D]' : 'bg-[#FFF7E6] border-[#E8C16C] hover:bg-[#FAEDD0] hover:border-[#A7843D]'} shadow-md`
                              : `${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] hover:bg-[#2A3441] hover:border-[#3A4553]' : 'bg-white border-gray-200 hover:bg-[#FFF7E6] hover:border-[#E8C16C] hover:shadow-md'}`
                        }
                        ${selectedDateYmd === ymd
                          ? `${isDark ? 'ring-2 ring-[#B59143]' : 'ring-2 ring-[#E8C16C]'}`
                          : ''
                        }
                      `}
                      onMouseEnter={() => d && setHoverDate(ymd)}
                      onMouseLeave={() => setHoverDate(null)}
                      onClick={() => {
                        if (!d) return;
                        setSelectedDateYmd(ymd);
                        if (isHoliday && canEdit && isEditMode) {
                          handleOpenExisting(ymd);
                        } else if (isHoliday && calendarPerms.isViewer) {
                          handleOpenExisting(ymd);
                        } else if (!isHoliday && canEdit && isEditMode) {
                          handleCreateHoliday(ymd);
                        }
                      }}
                    >
                      {d && (
                        <>
                          {/* Date number */}
                          <div className="absolute top-1 left-1">
                            <span
                              className={`
                              inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full
                              ${isToday
                                  ? `${isDark ? 'bg-[#B59143] text-black' : 'bg-[#B59143] text-white'} shadow-lg`
                                  : isHoliday
                                    ? `${isDark ? 'bg-[#B59143] text-black' : 'bg-[#B59143] text-white'}`
                                    : `${isDark ? 'text-white' : 'text-gray-700'}`
                                }
                            `}
                            >
                              {d.getDate()}
                            </span>
                          </div>

                          {/* Holiday indicator */}
                          {isHoliday && (
                            <div className="absolute bottom-1.5 left-1.5 right-1.5 hidden sm:block">
                              <div
                                className={`text-[9px] px-1.5 py-0.5 rounded-md truncate font-medium text-center border shadow-sm ${isDark
                                  ? 'text-black border-[#A7843D]'
                                  : 'text-white border-[#B59143]'
                                  }`}
                                style={{
                                  background: 'linear-gradient(90deg, #FBD282, #B59143)'
                                }}
                              >
                                Holiday
                              </div>
                            </div>
                          )}

                          {/* Today indicator */}
                          {isToday && !isHoliday && (
                            <div className="absolute bottom-1.5 left-1.5 right-1.5 hidden sm:block">
                              <div className={`${isDark ? 'bg-[#B59143] text-black' : 'bg-[#B59143] text-white'} text-[10px] px-1.5 py-0.5 rounded-md text-center font-medium`}>
                                Today
                              </div>
                            </div>
                          )}

                          {/* Hover tooltip */}
                          {isHoliday && hoverDate === ymd && (
                            <div className="absolute z-30 -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full">
                              <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-48 text-center">
                                <div className="font-medium mb-1">
                                  {getDateDisplay(ymd)}
                                </div>
                                <div className="text-gray-200">
                                  {holidays[ymd]?.reason || "Holiday"}
                                </div>
                                {/* Arrow */}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}

                          {/* Click indicator for empty dates */}
                          {!isHoliday &&
                            canEdit &&
                            isEditMode &&
                            hoverDate === ymd && (
                              <div className="absolute inset-2 flex items-center justify-center">
                                <div className="bg-gray-700 text-white text-xs px-2 py-1 rounded-md font-medium opacity-75">
                                  Click to add holiday
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Details 33% on md+ */}
            <div className="md:basis-1/3 md:max-w-[33.333%]">
              <div className={`md:sticky top-4 rounded-lg p-4 border ${isDark ? 'bg-[#14161B] border-[#2E2F33] text-white' : 'bg-white border-gray-200'}`}>
                {/* Monthly list */}
                <div className="">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      This month's holidays
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      {monthNames[month]} {year}
                    </div>
                  </div>
                  {(() => {
                    const monthHolidays = Object.values(holidays)
                      .filter((h) => {
                        const d = new Date(h.date);
                        return (
                          d.getFullYear() === year && d.getMonth() === month
                        );
                      })
                      .sort((a, b) => (a.date < b.date ? -1 : 1));
                    if (monthHolidays.length === 0) {
                      return (
                        <div className={`rounded-xl border border-dashed p-4 text-center ${isDark ? 'border-[#2E2F33] bg-[#0F1115]' : 'border-gray-200 bg-gradient-to-br from-purple-50 to-blue-50'}`}>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>
                            No holidays this month
                          </div>
                          <div className={`mt-2 text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white/70 border-gray-200'}`}>
                              <span className="text-[#B59143]">★</span>
                              <span className="italic">{randomQuote}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <ul className="space-y-2">
                        {monthHolidays.map((h) => (
                          <li key={h.id} className="flex items-start gap-2">
                            <span className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 text-[11px] font-semibold rounded-full ${isDark ? 'bg-[#2A3441] text-white' : 'bg-blue-100 text-blue-700'}`}>
                              {new Date(h.date).getDate()}
                            </span>
                            <div className="min-w-0">
                              <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                                {getDateDisplay(h.date)}
                              </div>
                              <div className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                {h.reason}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Holiday Modal */}
      {modalState && modalState.type === "create" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className={`rounded-lg shadow-xl w-full max-w-md mx-4 border ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Add Holiday
                </h3>
                <button
                  onClick={() => setModalState(null)}
                  className={`focus:outline-none rounded-full w-8 h-8 inline-flex items-center justify-center border ${isDark ? 'text-[#C3C9D3] border-[#2E2F33] hover:bg-[#2A3441]' : 'text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                  Date
                </label>
                <div className={`text-sm px-3 py-2 rounded-lg border ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33]' : 'text-gray-900 bg-gray-50 border-gray-200'}`}>
                  {getDateDisplay(modalState.date)}
                </div>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                  Holiday Reason
                </label>
                <textarea
                  className={`w-full rounded-lg p-3 h-24 resize-none focus:outline-none focus:ring-2 ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33] focus:ring-[#B59143]' : 'text-gray-900 border border-gray-300 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                  placeholder="Enter the reason for this holiday..."
                  value={modalState.reason}
                  onChange={(e) =>
                    setModalState({ ...modalState, reason: e.target.value })
                  }
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setModalState(null)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none ${isDark ? 'text-white bg-[#14161B] border border-[#2E2F33] hover:bg-[#2A3441]' : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCreate}
                  disabled={!modalState.reason.trim()}
                  className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none transition-colors ${modalState.reason.trim() ? (isDark ? 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-black' : 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-white') : 'opacity-60 cursor-not-allowed text-white bg-[#B59143]'}`}
                >
                  Add Holiday
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View/Edit Holiday Modal */}
      {modalState && modalState.type === "view" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className={`rounded-lg shadow-xl w-full max-w-md mx-4 border ${isDark ? 'bg-[#1A1C1F] border-[#2E2F33] text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Holiday Details
                  </h3>
                  {/* Delete button - Only show for admins */}
                  {canDelete && (
                    <button
                      onClick={handleDeleteHoliday}
                      className={`focus:outline-none p-1 rounded-full transition-colors ${isDark ? 'text-red-400 hover:bg-[#2A3441]' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                      title="Delete holiday"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setModalState(null)}
                  className={`focus:outline-none rounded-full w-8 h-8 inline-flex items-center justify-center border ${isDark ? 'text-[#C3C9D3] border-[#2E2F33] hover:bg-[#2A3441]' : 'text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                  Date
                </label>
                <div className={`text-sm px-3 py-2 rounded-lg border ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33]' : 'text-gray-900 bg-gray-50 border-gray-200'}`}>
                  {getDateDisplay(modalState.date)}
                </div>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                  Holiday Reason
                </label>
                {modalState.editing && canEdit ? (
                  <textarea
                    className={`w-full rounded-lg p-3 h-24 resize-none focus:outline-none focus:ring-2 ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33] focus:ring-[#B59143]' : 'text-gray-900 border border-gray-300 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                    placeholder="Enter the reason for this holiday..."
                    value={modalState.reason}
                    onChange={(e) =>
                      setModalState({ ...modalState, reason: e.target.value })
                    }
                  />
                ) : (
                  <div
                    className={`relative rounded-lg p-3 min-h-[96px] whitespace-pre-wrap ${canEdit ? 'cursor-pointer' : ''} transition-colors border ${isDark ? 'text-white bg-[#14161B] border-[#2E2F33] hover:bg-[#2A3441]' : 'text-gray-900 bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                    onClick={() =>
                      canEdit && setModalState({ ...modalState, editing: true })
                    }
                  >
                    {modalState.reason}
                    {canEdit && (
                    <button className={`absolute top-3 right-3 p-1 text-gray-400 ${isDark ? 'hover:text-[#FBD282]' : 'hover:text-[#B59143]'} transition-colors`}>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    )}
                  </div>
                )}
              </div>

              {/* Update button at bottom - Only show when editing */}
              {modalState.editing && (
                <div className="flex justify-end">
                  <button
                    onClick={handleUpdateHoliday}
                    disabled={!modalState.reason.trim() || isUpdating}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg focus:outline-none transition-colors ${modalState.reason.trim() && !isUpdating
                      ? `${isDark ? 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-black' : 'bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] text-white'}`
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      } ${isDark ? 'focus:ring-2 focus:ring-[#B59143]' : 'focus:ring-2 focus:ring-[#B59143] focus:ring-offset-2'}`}
                    title="Update holiday"
                  >
                    {isUpdating ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Update
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
